import React, { useCallback, useContext, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { Button, Tooltip, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import StockDamageService from 'services/StockDamageService';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import Info from 'components/utils/Alert/Info';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { ROUTES, SUBSCRIPTION_TYPE, USER_PERMISSIONS } from 'const';
import { UserContext } from 'Context/UserContext';
import { ShopContext } from 'Context/ShopContext';
import { InventoryContext } from 'Context/InventoryContext';
import { AlertMessagesContext } from 'react-alert-messages';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { saveAs } from 'file-saver';
import CsvHelper from 'helpers/CsvHelper';
import SyncIcon from '@mui/icons-material/Sync';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import DeleteIcon from '@mui/icons-material/Delete';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';
import { WarehouseContext } from 'Context/WarehouseContext';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';

function StockDamageHistory() {
  const { t: tCommon } = useTranslation("common");
  const { t: tReport } = useTranslation("report");
  const { user } = useContext(UserContext);
  const { shop } = useContext(ShopContext);
  const { products, getProducts, getStockBatches } = useContext(InventoryContext);
  const { getWarehouseProducts } = useContext(WarehouseContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [filterFromDate, setFilterFromDate] = useState(new Date());
  const [filterToDate, setFilterToDate] = useState(new Date());
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [deleteData, setDeleteData] = useState(false);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
  });

  const CSV_COLUMNS = {
    date: tCommon('Date'),
    plu: {
      title: tCommon('PLU'),
      getValue: (item) => item?.item?.plu,
    },
    item: {
      title: tCommon('Item Name'),
      getValue: (item) => item?.item?.name,
    },
    cost: {
      title: tCommon('Cost'),
      getValue: (item) => item?.item?.cost,
    },
    quantity: tCommon('Quantity'),
    description: tCommon('Description'),
  };

  const subscriptionType = shop?.subscription?.type;
  const userHasDamageWritePermission = user?.shop_permissions?.damage_permission === USER_PERMISSIONS.WRITE;

  const handleDeleteClick = (data) => setDeleteData(data);

  const handleDltStockDamage = async (row) => {
    setLoadingIndicator(true);
    try {
      await StockDamageService.deleteStockDamageHistory(row.id);
      loadStockDamageHistory().then();
      handleDeleteClick(false);

      const isPremium = shop?.subscription?.type === SUBSCRIPTION_TYPE.PREMIUM;
      const matchedProduct = (products || []).find((p) => p.id === row.item?.id);
      const hasBatchEnabled = !!(matchedProduct?.is_batch_enabled || row.item?.is_batch_enabled);
      const isWarehouseEnabled = !featureIsExpired(shop?.warehouse_validity);

      const promises = [getProducts({ useCache: false })];
      if (isPremium && hasBatchEnabled) {
        promises.push(getStockBatches({ useCache: false }));
      }
      if (isWarehouseEnabled) {
        promises.push(getWarehouseProducts({ useCache: false }));
      }
      await Promise.all(promises);

      postAlertMessage({
        text: tCommon('Damage deleted successfully'),
        type: 'success',
      });
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const _getLoadDamageHistoryRequestParams = useCallback(
    (start = filterFromDate, end = filterToDate) => {
      return {
        date_start: DateTimeHelpers.getDateYMD(start),
        date_end: DateTimeHelpers.getDateYMD(end),
      };
    },
    [filterFromDate, filterToDate]
  );

  const loadStockDamageHistory = useCallback(
    async (startDate = filterFromDate, endDate = filterToDate) => {
      setLoadingIndicator(true);
      try {
        const params = _getLoadDamageHistoryRequestParams(startDate, endDate);
        const resp = await StockDamageService.getStockDamageHistory(params);
        setData(resp);
      } catch (err) {
        postAlertMessage({ text: err.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    },
    [_getLoadDamageHistoryRequestParams, postAlertMessage, filterFromDate, filterToDate]
  );

  const toCsvBtnPressed = async () => {
    const csv = CsvHelper.getString(data, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `DAMAGES-${dateTime}.csv`);

    postAlertMessage({
      text: tCommon('Exported to excel successfully'),
      type: 'success',
    });
  };

  const generateStockDamageHistory = () => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('STOCK_DAMAGE');

    setFilterFromDate(dynamicStartDate);
    setFilterToDate(dynamicEndDate);

    loadStockDamageHistory(dynamicStartDate, dynamicEndDate);
    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: dynamicStartDate,
      filterToDate: dynamicEndDate,
    });
  };

  const isAnyChangeOnStockDamageFilters = useCallback(() => {
    return filterFromDate !== prevFilterData.filterFromDate || filterToDate !== prevFilterData.filterToDate;
  }, [filterFromDate, filterToDate, prevFilterData.filterFromDate, prevFilterData.filterToDate]);

  const _getFilteredDamages = (damages, searchKey) => {
    if (!searchKey) {
      return damages;
    }

    return damages.filter(
      (dta) =>
        dta.item.name.toLowerCase().includes(searchKey.toLowerCase()) ||
        dta.date.includes(searchKey.toLowerCase()) ||
        dta.item.plu.toString().includes(searchKey.toLowerCase())
    );
  };

  const _getDamagesSummary = (damages) => {
    let totalQuantity = 0;
    let totalAmount = 0;
    damages.forEach((damage) => {
      totalQuantity += damage.quantity;
      totalAmount += damage.quantity * damage.item.cost;
    });

    return {
      totalQuantity: ShopsHelper.getAmountFormatted(shop, totalQuantity),
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
    };
  };

  useEffect(() => {
    generateStockDamageHistory();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnStockDamageFilters());
  }, [isAnyChangeOnStockDamageFilters, isShowGenerateButton]);

  const headerData = [
    {
      label: tCommon('Date'),
      id: 'date',
      type: 'text',
    },
    {
      label: tCommon('PLU'),
      id: 'item.plu',
      type: 'text',
    },
    {
      label: tCommon('Item Name'),
      id: 'item.name',
      type: 'text',
    },
    {
      label: tCommon('Cost'),
      id: 'item.cost',
      type: 'text',
    },
    {
      label: tCommon('Quantity'),
      id: 'quantity',
      type: 'callback',
      viewRender: (item) => {
        return `${item.quantity} ${item.item.unit || ''}`;
      },
    },
    {
      label: tCommon('Total'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return ShopsHelper.getAmountFormatted(shop, item.quantity * item.item.cost);
      },
    },
  ];
  if (userHasDamageWritePermission) {
    headerData.push({
      label: tCommon('Actions'),
      id: 'deleteBtn',
      type: 'callback',
      viewRender: (item) => {
        return (
          
          <Tooltip title={tCommon('Delete')}>
            <IconButton onClick={() => handleDeleteClick(item)} size="small">
              <DeleteIcon className={styles.iconButton} />
            </IconButton>
          </Tooltip>
        );
      },
    });
  }

  const filteredDamages = _getFilteredDamages(data, searchText);
  const summary = _getDamagesSummary(filteredDamages);
  return (
    <div className={styles.contentWrapper}>
      <ConfirmPopup
        data={deleteData}
        handleClose={handleDeleteClick}
        handleOkay={handleDltStockDamage}
        successBtnName={tCommon('Delete')}
        alertTitle={tCommon('Confirm Delete')}
        alertContent={tReport('Deleted Stock Damage records can\'t be restored, do you want to delete the Stock Damage record?')}
      />
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Stock Damages')}<span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>
        {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
          <div style={{ justifyContent: 'flex-end' }}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Export As')}</label>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={styles.actionBtn}
              style={{ backgroundColor: '#00a65a', marginRight: 5 }}
              onClick={toCsvBtnPressed}
            >
              <ImportExportIcon className={styles.actionBtnIcon} />
              {tCommon('CSV')}
            </Button>
          </div>
        )}
      </div>

      {subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.BASIC && (
        <RestrictionInfo
          title={tCommon('Feature not available')}
          content={tCommon('To get stock damage options upgrade subscription to premium')}
        />
      )}
      {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
        <>
          <div className={styles.changeable}>
            <div className={styles.filterSec}>
              <div className={styles.headTitle}>
                <h2 className={styles.subTitle}>
                  {tReport('Damage History from {{from}} to {{to}}', {
                    from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                    to: DateTimeHelpers.convertDateToDMY(filterToDate),
                  })}
                </h2>
              </div>
              <div className={styles.filerInputSec}>
                {userHasDamageWritePermission && (
                  <Link to={ROUTES.NEW_DAMAGE} style={{ textDecorationColor: 'transparent' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      className={styles.actionBtn}
                      style={{ backgroundColor: '#ff851b' }}
                    >
                      <NoteAddIcon className={styles.actionBtnIcon} />
                      {tCommon('New Damage')}
                    </Button>
                  </Link>
                )}
                <div className={styles.searchSec}>
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                    }}
                    className={styles.searchInput}
                    placeholder={tCommon('Search items')}
                  />
                  <Tooltip title={tCommon('Search')} placement="bottom">
                    <IconButton size="small">
                      <SearchIcon className={styles.searchIcon} />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className={styles.actionButtons}>
              <div className={styles.filterDiv}>
                <DateTimeRange
                  setFilterFromDate={setFilterFromDate}
                  setFilterToDate={setFilterToDate}
                  storageKey="STOCK_DAMAGE"
                />
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Generate Report')}</label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    className={styles.actionBtn}
                    style={{ backgroundColor: '#00a65a' }}
                    onClick={generateStockDamageHistory}
                    disabled={isShowGenerateButton === false}
                  >
                    <SyncIcon className={styles.actionBtnIcon} />
                    {tCommon('Generate')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {isShowGenerateButton === null ? (
            <Info
              title={tCommon('Set filters and click generate button')}
              content={tReport(
                'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
              )}
            />
          ) : (data || loadingIndicator) &&
          ((filteredDamages && filteredDamages.length) || loadingIndicator ? (
            <div className={styles.tableContainer}>
              <Grayout open={isShowGenerateButton} />
              <DataTable
                columns={headerData}
                rows={filteredDamages || []}
                isLoading={loadingIndicator}
                summary={{
                  'item.name': tCommon('Total'),
                  quantity: summary.totalQuantity,
                  total: summary.totalAmount,
                }}
              />
            </div>
          ) : (
            !loadingIndicator && (
              <Info
                title={tCommon('You have no stock damages to list')}
                content={tCommon("You haven't any damages to list with given properties")}
              />
            )
          ))}
        </>
      )}
    </div>
  );
}

export default withConsoleBase(StockDamageHistory);
